import { requestWithCookieAuth, unAuthRequest } from "api";
import { DIAGNOSTICS_API_BASE } from "config";

export const EXTERNAL_SESSION = "external_session";

const getSessionObject = () => {
  const raw = sessionStorage.getItem(EXTERNAL_SESSION);
  return raw ? JSON.parse(raw) : null;
};

const setSessionObject = (obj) => {
  sessionStorage.setItem(EXTERNAL_SESSION, JSON.stringify(obj));
};

const generateSessionId = () => {
  const prefix = "external:";
  if (typeof crypto?.randomUUID === "function") {
    return `${prefix}${crypto.randomUUID()}`;
  }

  return `${prefix}${(
    [1e7] +
    "" +
    -1e3 +
    "" +
    -4e3 +
    "" +
    -8e3 +
    "" +
    -1e11
  ).replace(/[018]/g, (c) =>
    (
      parseInt(c) ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (parseInt(c) / 4)))
    ).toString(16),
  )}`;
};

export const getSessionID = (isExternal = true) => {
  let session = getSessionObject();

  if (!session) {
    session = { sessionId: generateSessionId() };
    setSessionObject(session);
  }

  return session.sessionId;
};

export const deleteExternalSession = () => {
  sessionStorage.removeItem(EXTERNAL_SESSION);
};

export const setConsent = (consent) => {
  let session = getSessionObject();

  if (!session) {
    session = { sessionId: generateSessionId() };
  }

  session.consent = consent;
  setSessionObject(session);
};

export const getConsent = () => {
  const session = getSessionObject();
  return session ? session.consent : undefined;
};

export const getDiagnostics = async () => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/`,
    { redirect: "follow" },
  );

  if (!response.ok) throw new Error("Failed to fetch diagnostics");
  return await response.json();
};

export const getDiagnosticById = async ({ id }) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${id}/`,
    {
      method: "GET",
      redirect: "follow",
      headers: {
        Accept: "application/json",
      },
    },
  );

  if (!response.ok) throw new Error("Failed to fetch diagnostic");
  return await response.json();
};

export const createDiagnostic = async ({
  name,
  description,
  client,
  owner,
  collaborators,
  background,
  beginningMessage,
  endingMessage,
  headerColor,
  headerLogoUrl,
  accentColor,
  customBrandingEnabled,
  demographicsQuestions,
  demographicsPlacement,
  demographicsQuestionOverrides,
}) => {
  const body = {
    name,
    description,
    client,
    collaborators,
    background,
    beginningMessage,
    endingMessage,
  };

  if (owner !== undefined) {
    body.owner = owner;
  }

  if (headerColor !== undefined) {
    body.headerColor = headerColor;
  }

  if (headerLogoUrl !== undefined) {
    body.headerLogoUrl = headerLogoUrl;
  }

  if (accentColor !== undefined) {
    body.accentColor = accentColor;
  }

  if (customBrandingEnabled !== undefined) {
    body.customBrandingEnabled = customBrandingEnabled;
  }

  if (demographicsQuestions !== undefined) {
    body.demographicsQuestions = demographicsQuestions;
  }

  if (demographicsPlacement !== undefined) {
    body.demographicsPlacement = demographicsPlacement;
  }

  if (demographicsQuestionOverrides !== undefined) {
    body.demographicsQuestionOverrides = demographicsQuestionOverrides;
  }

  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      redirect: "follow",
    },
  );

  if (!response.ok) {
    throw new Error("Failed to create diagnostic");
  }

  return await response.json();
};

export const updateDiagnostic = async ({ id, diagnostic }) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${id}/`,
    {
      method: "PUT",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(diagnostic),
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.detail ?? `Failed to update diagnostic (${response.status})`,
    );
  }

  return await response.json();
};

export const registerDiagnosticLogo = async (id, blobName) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${id}/logo/`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ blobName }),
      redirect: "follow",
    },
  );

  if (!response.ok) {
    throw new Error("Failed to register diagnostic logo");
  }

  return await response.json();
};

export const deleteDiagnosticLogo = async (id) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${id}/logo/`,
    {
      method: "DELETE",
      redirect: "follow",
    },
  );

  if (!response.ok) {
    throw new Error("Failed to delete diagnostic logo");
  }

  return await response.json();
};

export const deleteQuestionBank = async (diagnosticId) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${diagnosticId}/question-bank/`,
    {
      method: "DELETE",
      redirect: "follow",
    },
  );
  if (!response.ok && response.status !== 204) {
    throw new Error("Failed to delete question bank");
  }
};

export const deleteDiagnostic = async ({ id }) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${id}/`,
    {
      method: "DELETE",
      redirect: "follow",
    },
  );

  if (!response.ok) throw new Error("Failed to delete diagnostic");
  return await response.json();
};

export const updateDiagnosticOwner = async ({ id, owner }) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${id}/owner/`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(owner),
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.detail ?? `Failed to update diagnostic owner (${response.status})`,
    );
  }

  return await response.json();
};

export const closeDiagnostic = async ({ id }) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${id}/close/`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: { Accept: "application/json" },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to close diagnostic");
  }

  return await response.json();
};

export const closeDistributions = async ({ id, distributionIds }) => {
  if (!Array.isArray(distributionIds) || distributionIds.length === 0) {
    throw new Error(
      "closeDistribution: distributionIds (string[]) is required",
    );
  }

  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${id}/distributions/close/`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ distributionIds }),
    },
  );

  if (!response.ok) throw new Error("Failed to close distributions");
  return await response.json();
};

export const openDistributions = async ({ id, distributionIds }) => {
  if (!Array.isArray(distributionIds) || distributionIds.length === 0) {
    throw new Error("openDistribution: distributionIds (string[]) is required");
  }

  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${id}/distributions/open/`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ distributionIds }),
    },
  );

  if (!response.ok) throw new Error("Failed to open distributions");
  return await response.json();
};

export const updateDistributionSpeech = async ({
  diagnosticId,
  distributionId,
  speechEnabled,
}) => {
  const url = new URL(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${diagnosticId}/distributions/${distributionId}/speech/`,
  );
  url.searchParams.append("speechEnabled", String(speechEnabled));

  const response = await requestWithCookieAuth(url.toString(), {
    method: "PATCH",
    headers: { Accept: "application/json" },
    redirect: "follow",
  });

  if (!response.ok)
    throw new Error("Failed to update distribution speech setting");
  return await response.json();
};

export const updateDistributionLabel = async ({
  diagnosticId,
  distributionId,
  label,
  maxConnections,
}) => {
  const body = { label };
  if (maxConnections !== undefined) {
    body.maxConnections = maxConnections;
  }
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${diagnosticId}/distributions/${distributionId}/label/`,
    {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      redirect: "follow",
    },
  );

  if (!response.ok) throw new Error("Failed to update distribution label");
  return await response.json();
};

export const getDistributionRespodents = async ({ id }) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/answers/${id}/respondents/`,
    { method: "GET" },
  );

  if (!response.ok) throw new Error("Failed to fetch respondents");
  return await response.json();
};

export const createDiagnosticDistribution = async ({
  id,
  speechEnabled = true,
  label = "",
  maxConnections = 500,
}) => {
  const url = new URL(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${id}/create-distribution/`,
  );
  url.searchParams.append("speechEnabled", String(speechEnabled));

  const response = await requestWithCookieAuth(url.toString(), {
    method: "PATCH",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ label, maxConnections }),
    redirect: "follow",
  });

  if (!response.ok) throw new Error("Failed to create diagnostic distribution");
  return await response.json();
};

export const deleteDistributions = async ({ id, distributionIds }) => {
  if (!Array.isArray(distributionIds) || distributionIds.length === 0) {
    throw new Error(
      "deleteDistribution: distributionIds (string[]) is required",
    );
  }

  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${id}/delete-distributions/`,
    {
      method: "PATCH",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ distributionIds }),
    },
  );

  if (!response.ok) throw new Error("Failed to delete distributions");
  return await response.json();
};

export const deleteObjectiveFromDiagnostic = async ({ id, questionNumber }) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${id}/objectives/${questionNumber}/`,
    {
      method: "DELETE",
      redirect: "follow",
    },
  );

  return await response.json();
};

export const clearAllObjectivesFromDiagnostic = async ({ id }) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${id}/objectives/clear/`,
    {
      method: "DELETE",
      redirect: "follow",
    },
  );

  return await response.json();
};

export async function reorderObjectives({ id, orderList }) {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${id}/reorder-objectives/`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(orderList),
    },
  );
  if (!response.ok) throw new Error("Failed to reorder objectives");
  return response.json();
}

export async function addObjectiveToDiagnostic({ id, body }) {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${id}/add-objective/`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to create objective");
  }

  return response.json();
}

export async function generateObjectiveDescription({
  name,
  description,
  captureInput,
}) {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/objectives/generate-description/`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name, description, captureInput }),
      redirect: "follow",
    },
  );

  if (!response.ok) throw new Error("Failed to generate objective description");
  return await response.json();
}

export async function updateObjective({ id, questionNumber, body }) {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${id}/objectives/${questionNumber}/`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) throw new Error("Failed to update objective");
  return response.json();
}

export const submitExternalAnswer = async ({
  answerPayload,
  isFinal,
  distributionId = null,
}) => {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    "x-session-id": getSessionID(),
    distributionId: distributionId,
  };

  const response = await unAuthRequest(
    `${DIAGNOSTICS_API_BASE}/external-diagnostics/diagnostics/?isFinalQuestion=${isFinal}`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(answerPayload),
      redirect: "follow",
    },
  );

  if (!response.ok) throw new Error("Failed to submit answer");

  return await response.json();
};

export const skipObjectiveExternal = async ({ objectivePayload }) => {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    "x-session-id": getSessionID(),
  };

  const response = await unAuthRequest(
    `${DIAGNOSTICS_API_BASE}/external-diagnostics/objective/skip`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(objectivePayload),
      redirect: "follow",
    },
  );

  if (!response.ok) throw new Error("Failed to skip objective");

  return await response.json();
};

export const exportAnswersCSV = async ({ id }) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${id}/export/`,
    { redirect: "follow" },
  );
  if (!response.ok) {
    throw new Error("Failed to export answers");
  }

  const blob = await response.blob();

  const disposition = response.headers.get("Content-Disposition");
  let filename = "answers.csv";

  if (disposition && disposition.includes("filename=")) {
    const match = disposition.match(/filename\*?=(?:UTF-8''|["']?)([^"';]+)/);
    if (match?.[1]) {
      filename = decodeURIComponent(match[1]);
    }
  }

  return { blob, filename };
};

export const exportAnswersOfDistributionCSV = async ({
  diagnosticId,
  distributionId,
}) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${diagnosticId}/export/${distributionId}`,
    { redirect: "follow" },
  );
  if (!response.ok) {
    throw new Error("Failed to export answers");
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition");
  let filename = "answers.csv";

  if (disposition && disposition.includes("filename=")) {
    const match = disposition.match(/filename\*?=(?:UTF-8''|["']?)([^"';]+)/);
    if (match?.[1]) {
      filename = decodeURIComponent(match[1]);
    }
  }

  return { blob, filename };
};

export const exportAnswersOfDistributionsCSV = async ({
  diagnosticId,
  distributionIds,
}) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${diagnosticId}/export/bulk`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ distributionIds }),
      redirect: "follow",
    },
  );

  if (!response.ok) {
    throw new Error("Failed to export answers");
  }

  const blob = await response.blob();

  const disposition = response.headers.get("Content-Disposition");
  let filename = "answers.csv";

  if (disposition && disposition.includes("filename=")) {
    const match = disposition.match(/filename\*?=(?:UTF-8''|["']?)([^"';]+)/);
    if (match?.[1]) {
      filename = decodeURIComponent(match[1]);
    }
  }

  return { blob, filename };
};

export const getPublicDiagnosticUrl = async ({
  diagnosticId,
  distributionId,
}) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/${diagnosticId}/${distributionId}/public-url`,
    {
      headers: {
        Accept: "application/json",
      },
      redirect: "follow",
    },
  );

  if (!response.ok) throw new Error("Failed to get next objective");

  return await response.json();
};

export const getNextObjectiveExternal = async ({
  finished = false,
  distributionId = "",
  language = "English",
}) => {
  if (language == "Disabled") language = "English";
  const headers = {
    "x-session-id": getSessionID(),
  };

  const response = await unAuthRequest(
    `${DIAGNOSTICS_API_BASE}/external-diagnostics/objective/next?finished=${finished}&language=${language}`,
    {
      headers,
      redirect: "follow",
    },
  );

  if (!response.ok) throw new Error("Failed to get next objective");

  return await response.json();
};

export const getDiagnosticByIdExternal = async () => {
  const response = await unAuthRequest(
    `${DIAGNOSTICS_API_BASE}/external-diagnostics/`,
    {
      method: "GET",
      redirect: "follow",
      headers: {
        Accept: "application/json",
      },
    },
  );

  if (!response.ok) throw new Error("Failed to fetch diagnostic");
  return await response.json();
};

export const getSessionInfo = async () => {
  const headers = {
    "x-session-id": getSessionID(),
  };

  const response = await unAuthRequest(
    `${DIAGNOSTICS_API_BASE}/external-diagnostics/session-info`,
    {
      method: "GET",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        ...headers,
      },
    },
  );

  if (!response.ok) throw new Error("Failed to fetch session info");
  return await response.json();
};

export const getSpeechToken = async () => {
  const headers = {
    "x-session-id": getSessionID(),
  };

  const resp = await unAuthRequest(
    `${DIAGNOSTICS_API_BASE}/external-diagnostics/token`,
    {
      method: "GET",
      headers: { Accept: "application/json", ...headers },
      redirect: "follow",
    },
  );

  if (!resp.ok) {
    throw new Error("Failed to get speech token");
  }

  return await resp.json();
};

export const getSpeechTokenInternal = async (diagnosticId, distributionId) => {
  const headers = {
    "x-session-id": getSessionID(),
    distributionId: distributionId,
  };

  const resp = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/speech/${diagnosticId}/token`,
    {
      method: "GET",
      headers: { Accept: "application/json", ...headers },
      redirect: "follow",
    },
  );

  if (!resp.ok) {
    throw new Error("Failed to get speech token");
  }

  return await resp.json();
};

export const acceptConsent = async ({ consentPayload }) => {
  const headers = {
    "x-session-id": getSessionID(),
    Application: "application/json",
    "Content-Type": "application/json",
  };

  const response = await unAuthRequest(
    `${DIAGNOSTICS_API_BASE}/external-diagnostics/consent`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(consentPayload),
      redirect: "follow",
    },
  );

  if (!response.ok) throw new Error("Failed to accept consent");

  return await response.json();
};

export const areUrlParamsValid = async (diagnosticId, distributionId) => {
  const headers = {
    "x-session-id": getSessionID(),
  };

  const response = await unAuthRequest(
    `${DIAGNOSTICS_API_BASE}/external-diagnostics/validate_url_params/?code=${diagnosticId}`,
    {
      method: "GET",
      headers,
      redirect: "follow",
    },
  );

  if (response.status === 423) {
    // Closed distribution: the page shows a dedicated screen, not "Invalid URL".
    throw Object.assign(new Error("Distribution is closed"), {
      isDistributionClosed: true,
    });
  }
  if (!response.ok) throw new Error("Failed to validate URL params");

  return await response.json();
};

export const areUrlParamsValidInternal = async (
  diagnosticId,
  distributionId,
) => {
  const headers = {
    "x-session-id": getSessionID(),
  };

  const response = await unAuthRequest(
    `${DIAGNOSTICS_API_BASE}/diagnostics/validate_url_params?diagnosticId=${diagnosticId}&distributionId=${distributionId}`,
    {
      method: "GET",
      headers,
      redirect: "follow",
    },
  );

  if (!response.ok) throw new Error("Failed to validate URL params");

  return await response.json();
};

export const getAITopic = async ({ messages }) => {
  const response = await unAuthRequest(
    `${DIAGNOSTICS_API_BASE}/external-diagnostics/topicgeneration/`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      redirect: "follow",
      method: "POST",
      body: JSON.stringify({ messages }),
    },
  );
  return await response.json();
};

export const getAITopicInternally = async ({
  diagnosticId,
  distributionId,
  llmConfig,
  messages,
}) => {
  const response = await requestWithCookieAuth(
    `${DIAGNOSTICS_API_BASE}/diagnostics/topicgeneration/`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        distributionId: distributionId,
      },
      redirect: "follow",
      method: "POST",
      body: JSON.stringify({ llmConfig, messages }),
    },
  );
  return await response.json();
};

export const logoutPublicUser = async () => {
  const response = await unAuthRequest(
    `${DIAGNOSTICS_API_BASE}/external-diagnostics/logout/`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      redirect: "follow",
      method: "GET",
    },
  );
  return await response.json();
};

export const getPrivacyNoticeStatus = async () => {
  const headers = {
    "x-session-id": getSessionID(),
    Accept: "application/json",
  };

  const resp = await unAuthRequest(
    `${DIAGNOSTICS_API_BASE}/external-diagnostics/privacy_notice/status`,
    { method: "GET", headers, redirect: "follow" },
  );

  if (!resp.ok) {
    throw new Error("Failed to get privacy notice status");
  }

  return await resp.json();
};

export const recordPrivacyNotice = async (privacyNoticePayload) => {
  const headers = {
    "x-session-id": getSessionID(),
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  const resp = await unAuthRequest(
    `${DIAGNOSTICS_API_BASE}/external-diagnostics/privacy_notice`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(privacyNoticePayload),
      redirect: "follow",
    },
  );

  if (!resp.ok) {
    throw new Error("Failed to record privacy notice");
  }

  return await resp.json();
};
